// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { Typography, CircularProgress } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector } from 'Hooks';
import CreateMap from 'Features/map';
import PageToolbar from './PageToolbar';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { getAllFoldersThunk, moveProjectToFolderThunk } from 'Features/folderList/folderListThunk';
import FolderListItem from '../folderList/FolderListItem';
import stylesDef from './styles';
import { SetPageAction, SetProjectsAction } from './projectListActions';
import { GetProjectsThunk } from './projectListThunk';
import ProjectListItem from './ProjectListItem';
import TermsAndConditionsDialog from './Terms&ConditionsDialog';
import { selectFolderList, selectSelectedFolder } from '../folderList/folderListSelector';
import { PROJECTS_SECTION_TITLE } from 'Utils/constants';
import { ClearSelectedFolderAction } from 'Features/folderList/folderListActions';

const ProjectList = () => {
  const [showMapview, setShowMapview] = useState(false);
  const theme = useTheme();
  const styles = stylesDef(theme, showMapview);
  const dispatch = useAppDispatch();
  const { projectsLoading, projectsCount, loadAll, page, list, orderSortFilter } = useAppSelector(
    (state) => state.projectList,
  );
  const folderList = useAppSelector(selectFolderList);
  const { html } = useAppSelector(
    (state) => state.auth,
  );
  const selectedFolder = useAppSelector(selectSelectedFolder);

  useEffect(() => {
    if (selectedFolder?._id) {
      dispatch(ClearSelectedFolderAction());
    }
  }, [selectedFolder]);

  const showMapviewClick = () => {
    setShowMapview(true);
  };

  const showGridviewClick = () => {
    setShowMapview(false);
  };

  const handleMoveProjectToFolderSuccess = async (project?: IProject, folderId?: string) => {
    // Update the project's folderId with the new folderId
    let updatedProjects = list.map((p: IProject) => (p._id === project?._id ? { ...p, folderId } : p))
    if (orderSortFilter === 'unassigned') {
      updatedProjects = updatedProjects.filter((p: IProject) => !p?.folderId);
    }
    await dispatch(SetProjectsAction(updatedProjects));
  }

  const showNoProjects = projectsCount === 0 && list.length === 0;
  const showEmptyList = !projectsLoading && projectsCount > 0 && list.length === 0;
  const wrapperStyle : {} = showMapview ? styles.contentWrapperMap : styles.contentWrapperGrid;
  const listStyle : {} = showMapview ? styles.mapList : styles.gridList;
  const listWrapperStyle : {} = showMapview ? styles.mapListWrapper : styles.gridListWrapper;

  const handleDragEnd = async (event: DragEndEvent) => {
    const {active, over} = event;

    if (active.id && over?.id) {
      const response = await dispatch(moveProjectToFolderThunk(String(active.id), String(over.id)));
      if (response.success) {
        handleMoveProjectToFolderSuccess(response.data as IProject, String(over.id));
      }
    }
  }

  return (
    <Box sx={wrapperStyle}>
      <Box sx={styles.dashboardContent}>
        {html !== undefined && html !== 'undefined' && html !== 'null' && html !== null && (<TermsAndConditionsDialog />)}
        <Box sx={styles.mapviewLayoutLeft}>
          <PageToolbar
            showMapview={showMapview}
            showMapviewClick={showMapviewClick}
            showGridviewClick={showGridviewClick}
          />
          <DndContext onDragEnd={handleDragEnd}>
            <Box sx={listWrapperStyle}>
              {folderList.length > 0 && (
                <>
                  <Typography variant="h5" sx={styles.listTitle}>
                    Folders
                  </Typography>
                  <Box sx={listStyle}>
                    {folderList.map((folder: IFolder) => (
                      <FolderListItem
                        showMapview={showMapview}
                        folder={folder}
                        key={folder._id}
                      />
                    ))}
                  </Box>
                </>
              )}
              {list.length > 0 && (
                <>
                  <Typography variant="h5" sx={styles.listTitle}>
                    {PROJECTS_SECTION_TITLE[orderSortFilter]}
                  </Typography>
                  <Box sx={listStyle}>
                    {list.map((project: IProject) => (
                      <ProjectListItem
                        project={project}
                        key={project._id}
                        handleMoveProjectToFolderSuccess={handleMoveProjectToFolderSuccess}
                      />
                    ))}
                  </Box>
                  <Box sx={styles.loading}>
                    {!projectsLoading && (loadAll ? (
                      <Typography variant="h3">End of List</Typography>
                    ) : (
                      <>
                        <InView onChange={(inView) => {
                          if (inView) {
                            dispatch(SetPageAction(page + 1));
                            dispatch(GetProjectsThunk());
                          }
                        }}
                        />
                        <Typography variant="h3">Loading more...</Typography>
                      </>
                    ))}
                  </Box>
                </>
              )}
              {projectsLoading && (
                <Box sx={styles.loading}>
                  <CircularProgress size={50} />
                </Box>
              )}
            </Box>
          </DndContext>
          {
            showNoProjects && (
              <Box sx={styles.noProjects}>
                <Typography variant="h1">
                  You don't currently have any projects. Create a
                  <br />
                  project to get started!
                </Typography>
              </Box>
            )
          }
          {
            showEmptyList && (
              <Box sx={styles.noProjects}>
                <Typography variant="h1">Project not found</Typography>
              </Box>
            )
          }
        </Box>
        {showMapview && (
          <Box sx={styles.mapviewLayoutRight}>
            <CreateMap mode="dashboard" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProjectList;
